import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, UserPlus } from "lucide-react";
import { getRoleColor, getInitials } from "@/lib/roleColors";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logAuditAction } from "@/lib/permissions";

interface UnaMember {
  id: string;
  full_name: string;
  member_role: string;
  email: string | null;
  user_id: string | null;
}

interface AddAssigneeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: any;
  existingAssigneeIds: string[];
  onSuccess: () => void;
}

export function AddAssigneeDialog({
  open,
  onOpenChange,
  task,
  existingAssigneeIds,
  onSuccess,
}: AddAssigneeDialogProps) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState<UnaMember[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && task.una_id) {
      loadMembers();
    }
  }, [open, task.una_id]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('una_members')
        .select('id, full_name, member_role, email, user_id')
        .eq('una_id', task.una_id)
        .eq('status', 'active')
        .order('full_name');

      if (error) throw error;

      // Filter out already assigned members
      const availableMembers = (data || []).filter(
        m => !existingAssigneeIds.includes(m.id)
      );

      setMembers(availableMembers);
    } catch (error) {
      console.error('Error loading members:', error);
      toast({
        title: "Error",
        description: "Failed to load members",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(m =>
    m.full_name.toLowerCase().includes(search.toLowerCase()) ||
    m.member_role.toLowerCase().includes(search.toLowerCase())
  );

  const toggleMember = (memberId: string) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleAddAssignees = async () => {
    if (selectedMembers.length === 0) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('add-task-assignee', {
        body: {
          taskId: task.id,
          unaMemberIds: selectedMembers,
          unaId: task.una_id,
          taskTitle: task.task_title,
          taskDescription: task.task_description,
          taskCategory: task.category_type,
          dueDate: task.due_date,
        },
      });

      if (error) throw error;

      const selectedMemberNames = members
        .filter(m => selectedMembers.includes(m.id))
        .map(m => m.full_name)
        .join(', ');

      toast({
        title: "Assignees Added",
        description: `${selectedMemberNames} added to task and notified`,
      });

      if (task.una_id) {
        await logAuditAction(
          task.una_id,
          'add_task_assignees',
          'task',
          task.id,
          { assignee_count: selectedMembers.length, assignees: selectedMemberNames }
        );
      }

      onSuccess();
      onOpenChange(false);
      setSelectedMembers([]);
      setSearch("");
    } catch (error: any) {
      console.error('Error adding assignees:', error);
      toast({
        title: "Error",
        description: "Failed to add assignees",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Assignees to Task</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members by name or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Selected Members Preview */}
          {selectedMembers.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/50">
              <span className="text-sm font-medium">Selected ({selectedMembers.length}):</span>
              {selectedMembers.map(memberId => {
                const member = members.find(m => m.id === memberId);
                if (!member) return null;
                return (
                  <Badge key={memberId} variant="secondary">
                    {member.full_name}
                  </Badge>
                );
              })}
            </div>
          )}

          {/* Member List */}
          <ScrollArea className="h-[400px] border rounded-lg">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                Loading members...
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {members.length === 0
                  ? "All UNA members are already assigned"
                  : "No members found matching your search"}
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {filteredMembers.map((member) => {
                  const roleColors = getRoleColor(member.member_role);
                  const isSelected = selectedMembers.includes(member.id);

                  return (
                    <div
                      key={member.id}
                      onClick={() => toggleMember(member.id)}
                      className={`
                        flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all
                        ${isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-accent'}
                      `}
                    >
                      {/* Avatar */}
                      <div
                        className="w-10 h-10 rounded-full border-2 flex items-center justify-center font-bold flex-shrink-0"
                        style={{
                          borderColor: roleColors.border,
                          backgroundColor: roleColors.bg,
                          color: roleColors.text,
                        }}
                      >
                        {getInitials(member.full_name)}
                      </div>

                      {/* Member Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{member.full_name}</p>
                        <Badge
                          variant="outline"
                          className="text-xs"
                          style={{
                            borderColor: roleColors.border,
                            color: roleColors.text,
                          }}
                        >
                          {roleColors.name}
                        </Badge>
                        {member.email && (
                          <p className="text-xs text-muted-foreground mt-1">{member.email}</p>
                        )}
                      </div>

                      {/* Checkbox */}
                      <div className={`
                        w-5 h-5 rounded border-2 flex items-center justify-center
                        ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'}
                      `}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-primary-foreground" fill="currentColor" viewBox="0 0 12 12">
                            <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" />
                          </svg>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddAssignees}
            disabled={selectedMembers.length === 0 || submitting}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add {selectedMembers.length} Assignee{selectedMembers.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
